import { useTimeContext } from "../../../context/TimeContext";
import React, { memo, useEffect, useState } from "react";
import clsx from "clsx";
import {Link, useLocation, withRouter} from "react-router-dom";
import {
    getColumnSettingsLsKey,
    getGuidShortFormat,
    initTableColumns,
    preparePlayerPromoAmount
} from "../../../common/utils/common";
import ClipboardButton from "../../../common/cellData/CopyButton";
import Formatter from "../../../utils/Formatter";
import { SCREEN_SIZES } from "../../../utils/consts";
import i18n from "../../../i18n";
import TableSettingsModal from "../../CRM/modal/TableSettingsModal";
import useWindowSize from "../../../hooks/useWindowSize";
import { PROMOTION_TYPE, REWARD_TYPE, SPORT_PROMOTION_TYPE } from "../../Promotions/common/promotionConstants";
import * as moment from "moment/moment";
import useUserConfig from "../../../hooks/useUserConfig";
import NewCustomTable from "../../../components/NewCustomTable";
import basket from "../../../assets/images/common/basket.svg";
import {getLocalStorageFilter} from "../../../store/filters/reducer";

const COLUMN_IDS = {
    actions: 'actions',
    promotionId: 'promotionId',
    playerPromotionId: 'playerPromotionId',
    templateName: 'templateName',
    bonusName: 'bonusName',
    amount: 'amount',
    balance: 'balance',
    turnover: 'turnover',
    WR: 'WR',
    reward: 'reward',
    win: 'win',
    status: 'status',
    addedDate: 'addedDate',
    bonusCompletedDate: 'bonusCompletedDate',
    expiryDate: 'expiryDate',
    type: 'type',
    maxBonusWin: 'maxBonusWin',
    backOfficeUser: 'backOfficeUser',
};

const getDefaultColumns = () => {
    return [
        COLUMN_IDS.actions,
        COLUMN_IDS.promotionId,
        COLUMN_IDS.playerPromotionId,
        COLUMN_IDS.templateName,
        COLUMN_IDS.bonusName,
        COLUMN_IDS.amount,
        COLUMN_IDS.balance,
        COLUMN_IDS.turnover,
        COLUMN_IDS.WR,
        COLUMN_IDS.reward,
        COLUMN_IDS.win,
        COLUMN_IDS.status,
        COLUMN_IDS.addedDate,
        COLUMN_IDS.bonusCompletedDate,
        COLUMN_IDS.expiryDate,
        COLUMN_IDS.type,
        COLUMN_IDS.maxBonusWin,
        COLUMN_IDS.backOfficeUser,
    ];
};

const KEY_I18N_MAP = {
    [COLUMN_IDS.actions]: 'promotions.actions',
    [COLUMN_IDS.promotionId]: 'promotions.promotionId',
    [COLUMN_IDS.playerPromotionId]: 'promotions.playerPromotionId',
    [COLUMN_IDS.templateName]: 'promotions.templateName',
    [COLUMN_IDS.bonusName]: 'promotions.bonusName',
    [COLUMN_IDS.amount]: 'promotions.amount',
    [COLUMN_IDS.balance]: 'promotions.balance',
    [COLUMN_IDS.turnover]: 'promotions.turnover',
    [COLUMN_IDS.WR]: 'promotions.WR',
    [COLUMN_IDS.reward]: 'promotions.reward',
    [COLUMN_IDS.win]: 'promotions.win',
    [COLUMN_IDS.status]: 'promotions.status',
    [COLUMN_IDS.addedDate]: 'promotions.addedDate',
    [COLUMN_IDS.bonusCompletedDate]: 'promotions.bonusCompletedDate',
    [COLUMN_IDS.expiryDate]: 'promotions.expiryDate',
    [COLUMN_IDS.type]: 'promotions.type',
    [COLUMN_IDS.maxBonusWin]: 'promotions.maxBonusWin',
    [COLUMN_IDS.backOfficeUser]: 'promotions.backOfficeUser',
};

const TdElem = ({
                    item,
                    field,
                    timeFormat,
                    generalCurrency,
                    onCancelBonus
                }) => {
    const size = useWindowSize();
    const { adjustAmountWithUserCurrency } = useUserConfig();

    const {id, promotion} = item;
    const validityDays = +promotion?.settings?.validityDays;
    const isRewardType = promotion?.settings?.configuration?.rewards?.[0]?.type === REWARD_TYPE.PrizeWagering &&
        item.status === 'Completed';
    const sumPendingWinsFS = item.win;

    const {amountValue, amountCurrency} = preparePlayerPromoAmount(item, generalCurrency);
    const formattedAmountValue =
        new Intl.NumberFormat().format(isNaN(amountValue) ? '0' : amountValue);

    const bonusLimit = promotion?.settings?.configuration?.bonusLimit;
    const bonusLimitMulti = promotion?.settings?.configuration?.bonusLimitMultiplier;
    const initialNumOfSpins = promotion?.settings?.configuration?.numberOfSpins;
    const promoType = item?.promotion?.settings?.type;
    let bonusLimitValue;
    let bonusLimitCurrency;

    if (typeof bonusLimit === 'object') {
        const currencies = Object.keys(bonusLimit);
        if (currencies.length > 0) {
            bonusLimitCurrency = currencies.includes(generalCurrency) ? generalCurrency : '';
            bonusLimitValue = bonusLimit[bonusLimitCurrency || '-'];
        }
    } else if (!bonusLimit || bonusLimit === '') {
        bonusLimitValue = amountValue * bonusLimitMulti
    } else {
        bonusLimitValue = bonusLimit;
    }

    const endDate = item?.endDate
        ? moment(item?.endDate)
        : moment(item?.activationDate).add(validityDays, 'd');

    switch (field) {
        case COLUMN_IDS.actions:
            return <td className="action-btns">
                <img
                    className="action-btn"
                    src={basket}
                    alt="Basket"
                    onClick={() => onCancelBonus(id)}
                />
            </td>
        case COLUMN_IDS.promotionId:
            return <td className='title__link'>
                <div>
                    <Link
                        to={`/promotions/${item.promotion?.id}`}
                        className="me-2 app-text-primary"
                    >
                        {getGuidShortFormat(item.promotion?.id)}
                    </Link>
                    <ClipboardButton text={item.promotion?.id}/>
                </div>
            </td>
        case COLUMN_IDS.playerPromotionId:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.player.promotionId')}: </span>
                )}
                <div>
                    {getGuidShortFormat(item.id)}
                    <ClipboardButton text={item.id}/>
                </div>
            </td>
        case COLUMN_IDS.templateName:
            return <td className='title__top'>
                <span className='title__croped'>{item.promotion?.name}</span>
            </td>
        case COLUMN_IDS.bonusName:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.bonusName')}: </span>
                )}
                {item.name}
            </td>
        case COLUMN_IDS.amount:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.amount')}: </span>
                )}
                {promoType === PROMOTION_TYPE.FREE_SPINS || promoType === SPORT_PROMOTION_TYPE.DEPOSIT
                    ? initialNumOfSpins
                    : adjustAmountWithUserCurrency(formattedAmountValue)
                }</td>
        case COLUMN_IDS.balance:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.balance')}: </span>
                )}
                {promoType === PROMOTION_TYPE.FREE_SPINS ? (
                    <>
                        {item?.balance || 0}
                    </>
                ) : (
                    <>
                        {adjustAmountWithUserCurrency(item?.balance)}
                    </>
                )}
            </td>
        case COLUMN_IDS.turnover:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.turnover')}: </span>
                )}

                {adjustAmountWithUserCurrency(item?.turnOver)}
            </td>
        case COLUMN_IDS.WR:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.WR')}: </span>
                )}

                {adjustAmountWithUserCurrency(item?.wageringRequired)}
            </td>
        case COLUMN_IDS.reward:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.reward')}: </span>
                )}

                {adjustAmountWithUserCurrency(item?.result?.reward)}
            </td>
        case COLUMN_IDS.win:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.win')}: </span>
                )}
                {
                    isRewardType && item.result?.reward
                        ? '—'
                        : ((item.result?.pendingWin || item.result?.pendingWin === 0) ||
                            (item.result?.win || item.result?.win === 0))
                            ? adjustAmountWithUserCurrency(item.result.pendingWin || item.result.win)
                            : '—'
                }
            </td>
        case COLUMN_IDS.status:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.status')}: </span>
                )}
                {i18n.t(`promotion.statuses.${item?.status}`)}
            </td>
        case COLUMN_IDS.addedDate:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.addedDate')}: </span>
                )}
                {item.activationDate
                    ? Formatter.displayDateTimeSeconds(timeFormat.parseTime(item.activationDate))
                    : '—'
                }
            </td>
        case COLUMN_IDS.bonusCompletedDate:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.bonusCompletedDate')}: </span>
                )}
                {item.result?.time
                    ? Formatter.displayDateTimeSeconds(timeFormat.parseTime(item.result?.time))
                    : '—'
                }
            </td>
        case COLUMN_IDS.expiryDate:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.expiryDate')}: </span>
                )}
                {endDate
                    ? Formatter.displayDateTimeSeconds(timeFormat.parseTime(endDate))
                    : '—'
                }
            </td>
        case COLUMN_IDS.type:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.type')}: </span>
                )}
                {promotion?.settings?.type}
            </td>
        case COLUMN_IDS.maxBonusWin:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.maxBonusWin')}: </span>
                )}

                {adjustAmountWithUserCurrency(bonusLimitValue)}
            </td>
        case COLUMN_IDS.backOfficeUser:
            return <td>
                {size.width <= SCREEN_SIZES.MAX_TABLET_PORTRAIT && (
                    <span>{i18n.t('promotions.backOfficeUser')}: </span>
                )}
                {item?.backOfficeUser || '—'}
            </td>
        default:
            return null;
    }
};

const PromotionsTabTable = (props) => {
    const {
        list,
        isShowTableSettings = false,
        onCloseTableSettings = () => {},
        onCancelBonus,
    } = props;

    const {timeFormat} = useTimeContext();
    const [columns, setColumns] = useState([]);
    const [activeColumns, setActiveColumns] = useState(getDefaultColumns());
    const [defaultColumns, setDefaultColumns] = useState(getDefaultColumns());
    const [activeRow, setActiveRow] = useState(false);
    const location = useLocation();
    const filterValues = getLocalStorageFilter();

    useEffect(() => {
        initTableColumns(`/${location.search}`, getDefaultColumns, setActiveColumns);
    }, [location.search]);

    useEffect(() => {
        const columnsData = activeColumns.map((key) => ({
            label: i18n.t(KEY_I18N_MAP[key]),
            id: key,
        }));

        setColumns(columnsData);
    }, [activeColumns])

    const handleChangeColumns = (columnsSequence) => {
        onCloseTableSettings();

        // Apply columnsSequence
        if (columnsSequence.length > 1) {
            setActiveColumns(columnsSequence);

            const lsKey = getColumnSettingsLsKey(`/${location.search}`);
            localStorage.setItem(lsKey, columnsSequence.join(','));
        }
    }

    setTimeout(() => {
        document.querySelector('.app-table-wrapper')?.scrollTo({ top: 0 });
    }, 200);

    const handleToggle = (index) => {
        activeRow === index ? setActiveRow(null) : setActiveRow(index)
    }

    if (list?.length === 0 && !filterValues.length) {
        return <div className='noGamePlayData'>
            {i18n.t('tabs.NoActiveBonuses')}
        </div>
    }

    return (
        <>
            <NewCustomTable
                columns={columns}
                className={'mb-1 responsiveTable'}
            >
                {list?.map((item, index) => (
                    <tr
                        key={item.id}
                        className={clsx("text-center", {open: activeRow === index})}
                        onClick={() => handleToggle(index)}
                    >
                        {activeColumns.map((column) => (
                            <TdElem
                                key={`${column}_${index}`}
                                item={item}
                                field={column}
                                timeFormat={timeFormat}
                                onCancelBonus={onCancelBonus}
                            />
                        ))}
                    </tr>
                ))}
            </NewCustomTable>

            {isShowTableSettings && (
                <TableSettingsModal
                    onHide={onCloseTableSettings}
                    onConfirm={handleChangeColumns}
                    activeColumns={activeColumns}
                    defaultColumns={defaultColumns}
                    allColumns={defaultColumns}
                    popupTitle={i18n.t('crm.tableSettings')}
                    translatesObj={KEY_I18N_MAP}
                />
            )}
        </>
    )
}

export default memo(withRouter(PromotionsTabTable));